/** leads/calc.ts — 가능연도 계산 프론트 미러(미리보기용, 확정값은 백엔드 계산) + 엑셀 행 파서 */
import type { EligibleYear, LeadRowInput } from './types';

export function claimDeadline(taxYear: number): Date {
  return new Date(taxYear + 6, 4, 31, 23, 59, 59);
}

export function eligibleYears(investYear: number, ventureFrom?: string | null, ventureTo?: string | null, today?: Date): EligibleYear[] {
  const now = today || new Date();
  if (!investYear || investYear < 1990 || investYear > now.getFullYear()) return [];
  const from = ventureFrom ? new Date(ventureFrom) : null;
  const to = ventureTo ? new Date(ventureTo) : null;
  const out: EligibleYear[] = [];
  for (let i = 0; i < 3; i++) {
    const y = investYear + i;
    const ys = new Date(y, 0, 1);
    const ye = new Date(y, 11, 31, 23, 59, 59);
    const ventureOk = (!from || from <= ye) && (!to || to >= ys);
    const dl = claimDeadline(y);
    if (ventureOk && dl >= now) {
      out.push({ year: y, deadline: dl.toISOString().slice(0, 10), daysLeft: Math.floor((dl.getTime() - now.getTime()) / 86400000) });
    }
  }
  return out;
}

/** 저장된 eligible_years(등록 시점 스냅샷) → 조회 시점 재계산·도과 제외. 표시 전 반드시 경유 */
export function freshYears(list?: EligibleYear[], today?: Date): EligibleYear[] {
  const now = today || new Date();
  return (list || [])
    .map((y) => ({ year: y.year, deadline: y.deadline, daysLeft: Math.floor((new Date(y.deadline + 'T23:59:59').getTime() - now.getTime()) / 86400000) }))
    .filter((y) => y.daysLeft >= 0);
}

/** 엑셀 헤더 별칭 → 표준 필드 매핑 (정보공개청구 엑셀 컬럼명 편차 흡수) */
const HEADER_MAP: Record<string, string> = {
  '사업자등록번호': 'biz_no', '사업자번호': 'biz_no',
  '법인등록번호': 'corp_no', '법인번호': 'corp_no',
  '법인명': 'corp_name', '기업명': 'corp_name', '회사명': 'corp_name', '업체명': 'corp_name',
  '대표자명': 'ceo_name', '대표자': 'ceo_name', '대표': 'ceo_name',
  '투자연도': 'invest_year', '증자연도': 'invest_year', '자본금증자연도': 'invest_year',
  '투자금액': 'invest_amount', '증자금액': 'invest_amount',
  '벤처시작일': 'venture_from', '벤처유효시작': 'venture_from', '유효기간시작': 'venture_from',
  '벤처종료일': 'venture_to', '벤처유효종료': 'venture_to', '유효기간종료': 'venture_to',
  '주소': 'address', '법인주소': 'address', '소재지': 'address',
  '연락처': 'phone', '전화번호': 'phone', '휴대폰': 'phone',
  '이메일': 'email', '메모': 'memo', '비고': 'memo',
};

function normDate(v: unknown): string | null {
  if (v == null || v === '') return null;
  // 버그수정(2026-08-11): SheetJS Date는 로컬 자정 → toISOString이 KST(UTC+9)에서 전날로 밀림. 로컬 기준 변환.
  if (v instanceof Date) { const p = (n: number) => String(n).padStart(2, '0'); return v.getFullYear() + '-' + p(v.getMonth() + 1) + '-' + p(v.getDate()); }
  const s = String(v).trim().replace(/[./]/g, '-');
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return m[1] + '-' + m[2].padStart(2, '0') + '-' + m[3].padStart(2, '0');
  return null;
}

export interface ParsedRow { row: LeadRowInput | null; error?: string; raw: Record<string, unknown> }

/** SheetJS sheet_to_json 결과(헤더행 객체 배열) → LeadRowInput 배열 */
export function parseExcelRows(objs: Record<string, unknown>[]): ParsedRow[] {
  return objs.map((raw) => {
    const std: Record<string, unknown> = {};
    Object.entries(raw).forEach(([k, v]) => {
      const key = HEADER_MAP[String(k).replace(/\s/g, '')];
      if (key) std[key] = v;
    });
    const bizNo = String(std.biz_no || '').replace(/[^0-9]/g, '');
    const corpName = String(std.corp_name || '').trim();
    const investYear = parseInt(String(std.invest_year || ''), 10);
    if (bizNo.length !== 10) return { row: null, error: '사업자번호 10자리 아님', raw };
    if (!corpName) return { row: null, error: '법인명 없음', raw };
    if (!investYear || investYear < 1990) return { row: null, error: '투자연도 오류', raw };
    return {
      raw,
      row: {
        biz_no: bizNo, corp_name: corpName,
        ceo_name: String(std.ceo_name || '').trim(),
        address: String(std.address || '').trim(),
        phone: String(std.phone || '').trim(),
        email: String(std.email || '').trim(),
        memo: String(std.memo || '').trim(),
        detail: {
          invest_year: investYear,
          invest_amount: std.invest_amount != null && std.invest_amount !== '' ? Number(String(std.invest_amount).replace(/[^0-9.]/g, '')) : null,
          venture_from: normDate(std.venture_from),
          venture_to: normDate(std.venture_to),
        },
      },
    };
  });
}

export function fmtAmount(n: number | null | undefined): string {
  if (n == null) return '-';
  if (n >= 1000000000000) return (n / 1000000000000).toFixed(1).replace(/\.0$/, '') + '조';
  if (n >= 100000000) return (n / 100000000).toFixed(1).replace(/\.0$/, '') + '억';
  if (n >= 10000) return Math.round(n / 10000).toLocaleString() + '만';
  return n.toLocaleString();
}

/** 전화번호 표기 정규화 — backend/services/phoneFmt.normPhone 과 같은 규칙 (2026-09-21 사용자 지시)
 *  앞의 국가번호 제거('82-070-4801-0048' → '070-4801-0048') · 하이픈 없는 숫자열에 하이픈('07048010048' → '070-4801-0048') · 뒤 내선 메모는 유지 */
function hyphenate(d: string): string | null {
  if (/^1[5-9]\d{6}$/.test(d)) return d.slice(0, 4) + '-' + d.slice(4);
  if (/^02\d{7,8}$/.test(d)) return '02-' + d.slice(2, d.length - 4) + '-' + d.slice(-4);
  if (/^0(30|50)\d{8,9}$/.test(d)) return d.slice(0, 4) + '-' + d.slice(4, d.length - 4) + '-' + d.slice(-4);
  if (/^0\d{9,10}$/.test(d)) return d.slice(0, 3) + '-' + d.slice(3, d.length - 4) + '-' + d.slice(-4);
  return null;
}
export function fmtPhone(v: string | number | null | undefined): string {
  let t = String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  if (!t) return '';
  t = t.replace(/^\(?\+?\s*(?:00)?82\)?(?:[-\s.)]+|(?=0))/, '');
  const m = t.match(/^([\d()+\-\s.]*\d)(.*)$/);
  if (!m) return t;
  const head = m[1]; const rest = m[2].trim();
  let d = head.replace(/\D/g, '');
  if (!d) return t;
  if (!d.startsWith('0') && !/^1[5-9]\d{6}$/.test(d)) d = '0' + d;
  const out = hyphenate(d) || head.replace(/[^\d-]/g, '').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');
  return rest ? out + (/^[~,/]/.test(rest) ? '' : ' ') + rest : out;
}

/** 적는 동안 하이픈 자동 (2026-10-19 지도사 「하이픈 자동 들어가게」) — 숫자 · 하이픈만 적은 경우에만(「내선 12」 같은 글이 섞이면 그대로)
 *  010-1234-5678 · 02-123-4567 · 031-123-4567 · 1588-1234 · 지우는 중에도 자연스럽게(뒤 하이픈 안 남김) */
export function typingPhone(v: string): string {
  const t = String(v || '');
  if (/[^\d\-\s]/.test(t)) return t;
  const d = t.replace(/\D/g, '').slice(0, 11);
  if (/^1[5-9]/.test(d)) return d.length <= 4 ? d : d.slice(0, 4) + '-' + d.slice(4, 8);
  if (d.startsWith('02')) {
    if (d.length <= 2) return d;
    if (d.length <= 5) return '02-' + d.slice(2);
    if (d.length <= 9) return '02-' + d.slice(2, 5) + '-' + d.slice(5);
    return '02-' + d.slice(2, 6) + '-' + d.slice(6, 10);
  }
  if (d.length <= 3) return d;
  if (d.length <= 6) return d.slice(0, 3) + '-' + d.slice(3);
  if (d.length <= 10) return d.slice(0, 3) + '-' + d.slice(3, 6) + '-' + d.slice(6);
  return d.slice(0, 3) + '-' + d.slice(3, 7) + '-' + d.slice(7);
}

/** tel: 링크용 정제 — 표기 정규화 후 내선표기·공백·괄호 제거 (예: "02-123-4567(내선102)" → 통화 실패 방지) */
export function telHref(phone: string): string {
  return 'tel:' + fmtPhone(phone).replace(/\(.*$/, '').replace(/[^0-9+]/g, '');
}

export function fmtBizNo(b: string): string {
  return b.length === 10 ? b.slice(0, 3) + '-' + b.slice(3, 5) + '-' + b.slice(5) : b;
}

/** 법인등록번호 13자리 → XXXXXX-XXXXXXX (2026-08-19) */
export function fmtCorpNo(c: string | null | undefined): string {
  const d = String(c || '').replace(/[^0-9]/g, '');
  return d.length === 13 ? d.slice(0, 6) + '-' + d.slice(6) : (c || '');
}
